import { memo, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as RecentIcon } from "../../../../assets/content-overview-images/recent-clock.svg";

import { ReactComponent as TaskIcon } from "../../../../assets/content-overview-images/dailyTasks.svg";
import { ReactComponent as ArticleIcon } from "../../../../assets/content-overview-images/articles.svg";
import { ReactComponent as FactsIcon } from "../../../../assets/content-overview-images/facts.svg";
import { ReactComponent as ScenariosIcon } from "../../../../assets/content-overview-images/scenarios.svg";

import { ContentOverviewBlock } from "../../../../components/content-overview-block/content-overview-block";
import { Drawer } from "../../../../components/drawer/drawer";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";

import "./content-recent.css";

const TYPE_META = {
  dailyTask: {
    Icon: TaskIcon,
    label: "Task",
    buildHref: (projectCode, id) => `/${projectCode}/content/tasks/${id}`,
  },
  article: {
    Icon: ArticleIcon,
    label: "Article",
    buildHref: (projectCode, id) => `/${projectCode}/content/articles/${id}`,
  },

  fact: {
    Icon: FactsIcon,
    label: "FitFact",
    buildHref: (projectCode, id) => `/${projectCode}/content/fitfacts/${id}`,
  },
  scenario: {
    Icon: ScenariosIcon,
    label: "Scenario",
    buildHref: (projectCode, id) => `/${projectCode}/content/scenarios/${id}`,
  },
  onboardingSurvey: {
    Icon: ScenariosIcon,
    label: "Onboarding",
    buildHref: (projectCode, id) => `/${projectCode}/content/onboarding-surveys/${id}`,
  },

  category: {
    Icon: ArticleIcon,
    label: "Category",
    buildHref: (projectCode, id) => `/${projectCode}/content/categories/${id}`,
  },
  rule: {
    Icon: TaskIcon,
    label: "Rule",
    buildHref: (projectCode, id) => `/${projectCode}/content/rules/${id}`,
  },
  lifePoint: {
    Icon: FactsIcon,
    label: "LifePoint",
    buildHref: (projectCode, id) => `/${projectCode}/content/life-points/${id}`,
  },
};

const formatDate = (d) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(d));
  } catch {
    return String(d);
  }
};

const openNew = (href) => {
  if (!href) return;
  window.open(href, "_blank", "noopener,noreferrer");
};

const RecentList = memo(({ items, mode, projectCode, limit, bordered }) => {
  const visible = limit != null ? (items || []).slice(0, limit) : items || [];

  const handleOpen = (item) => {
    const id = item?.linkId;
    const type = item?.type;
    if (!id || !type) return;

    const meta = TYPE_META[type];
    if (!meta?.buildHref) return;

    openNew(meta.buildHref(projectCode, id));
  };

  return (
    <div className={bordered ? "content-recent-list bordered" : "content-recent-list"}>
      {visible.map((item) => {
        const meta = TYPE_META[item.type] || {};
        const Icon = meta.Icon;
        const typeLabel = meta.label || item.type;

        const dateLabel =
          mode === "added"
            ? `added: ${formatDate(item.date)}`
            : `last modified: ${formatDate(item.date)}`;

        return (
          <div
            key={`${item.type}-${item.linkId || item.id}-${item.date}`}
            className="content-recent-item"
            role="button"
            tabIndex={0}
            onClick={() => handleOpen(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleOpen(item);
            }}
            title={item.title || ""} // hover tooltip
          >
            <div className="content-recent-item-left">
              <div className="content-recent-item-iconWrap">
                {Icon ? <Icon className="content-recent-item-icon" /> : null}
              </div>
            </div>

            <div className="content-recent-item-right">
              <div className="content-recent-item-title">{item.title || "—"}</div>
              <div className="content-recent-item-desc">
                {typeLabel}, {dateLabel}
              </div>
            </div>
          </div>
        );
      })}

      {visible.length === 0 ? <div className="content-recent-empty">No items yet</div> : null}
    </div>
  );
});

const RecentColumn = memo(({ title, items, mode, onViewAll, projectCode }) => {
  return (
    <div className="content-recent-col">
      <div className="content-recent-col-title">{title}</div>
      <div className="content-recent-divider" />

      <RecentList items={items} mode={mode} projectCode={projectCode} limit={4} bordered={false} />

      <div
        className="content-recent-col-title content-recent-viewall"
        role="button"
        tabIndex={0}
        onClick={onViewAll}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onViewAll?.();
        }}
      >
        View all
      </div>
    </div>
  );
});

export const ContentRecent = memo(({ recent, loading, error, defaultOpen, onToggle }) => {
  const { projectCode } = useParams();

  const [drawer, setDrawer] = useState({ open: false, mode: null }); // mode: "added" | "modified"

  const data = useMemo(() => {
    const added = Array.isArray(recent?.added) ? recent.added : [];
    const modified = Array.isArray(recent?.modified) ? recent.modified : [];

    const norm = (x) => ({
      ...x,
      date: x?.date ?? null,
      linkId: x?.linkId ?? x?.activeId ?? x?.pendingId ?? x?.id ?? null,
    });

    return {
      lastAdded: added.map(norm),
      lastModified: modified.map(norm),
    };
  }, [recent]);

  const drawerTitle = drawer.mode === "added" ? "Last added" : "Last modified";
  const drawerItems = drawer.mode === "added" ? data.lastAdded : data.lastModified;

  return (
    <>
      <ContentOverviewBlock title="Recent" icon={<RecentIcon />} defaultOpen={defaultOpen} onToggle={onToggle}>
        <div className="content-recent-grid">
          <RecentColumn
            title="Last added"
            items={data.lastAdded}
            mode="added"
            projectCode={projectCode}
            onViewAll={() => setDrawer({ open: true, mode: "added" })}
          />
          <RecentColumn
            title="Last modified"
            items={data.lastModified}
            mode="modified"
            projectCode={projectCode}
            onViewAll={() => setDrawer({ open: true, mode: "modified" })}
          />
        </div>

        {loading ? <div className="content-recent-hint">Loading…</div> : null}
        {error ? <div className="content-recent-hint error">Failed to load recent</div> : null}
      </ContentOverviewBlock>

      <Drawer open={drawer.open} closable={false} onClose={() => setDrawer({ open: false, mode: null })}>
        <DrawerHeader title={drawerTitle} onClose={() => setDrawer({ open: false, mode: null })} />

        <div className="content-recent-drawer-body">
          <RecentList items={drawerItems} mode={drawer.mode} projectCode={projectCode} limit={null} bordered={true} />
        </div>
      </Drawer>
    </>
  );
});